/**
 * TG-C — drag-and-drop charging-curve editor.
 *
 * An interactive SVG plot of the DC charging curve (x = SoC 0–100 %, y = kW,
 * 0–yMax). It edits the SAME `{ socPct, powerKw }[]` the numeric table edits —
 * the curve that `domain/chargePowerKwAtSoc` interpolates each SoC tick — so a
 * shape drawn here drives the transaction's power directly.
 *
 *   • double-click empty area → add a point at that (SoC, kW)
 *   • drag a point           → move it and reshape the piecewise-linear curve
 *   • click a point          → select; Remove button / Delete|Backspace deletes
 *   • arrow keys             → nudge the selected point (Shift = ×5)
 *
 * The connecting polyline includes the flat held segments below the first and
 * above the last breakpoint, mirroring the interpolation's clamp-at-the-ends
 * behaviour. Pure point-math lives in `@/lib/curveMath` (unit-tested without a
 * DOM); this component is the thin DOM/pointer shell over it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  addPoint,
  clampPoint,
  movePoint,
  niceCeil,
  removePoint,
  snapPoint,
  sortCurve,
  type CurvePoint,
} from '@/lib/curveMath';

const W = 360;
const H = 220;
const PAD_L = 34; // y-axis labels
const PAD_R = 8;
const PAD_T = 10;
const PAD_B = 22; // x-axis labels
const PLOT_W = W - PAD_L - PAD_R;
const PLOT_H = H - PAD_T - PAD_B;
const PLOT_BOTTOM = H - PAD_B;

interface Props {
  /** Current curve points (numeric). Need not be sorted. */
  readonly points: readonly CurvePoint[];
  /** Flat max-DC cap (kW), used to size the y-axis when the curve is low/empty. */
  readonly maxDcKw: number;
  /** Commit a new (sorted, snapped) curve. */
  readonly onChange: (points: CurvePoint[]) => void;
  readonly testId?: string;
}

function serialize(points: readonly CurvePoint[]): string {
  return points.map((p) => `${p.socPct}:${p.powerKw}`).join('|');
}

export function CurveEditor({ points, maxDcKw, onChange, testId }: Props): JSX.Element {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const draggingRef = useRef<number | null>(null);
  const lastEmitted = useRef<string>('');
  const [working, setWorking] = useState<CurvePoint[]>(() => sortCurve(points));
  const [selected, setSelected] = useState<number | null>(null);
  // Mirror of `working` kept current SYNCHRONOUSLY (refs, unlike state/effects,
  // are readable within the same event tick). pointerdown→move→up can fire in
  // one synchronous burst (synthetic drags, fast input) before React re-renders,
  // so the pointer-up handler must read this ref, not the stale `working`
  // closure, to commit the actually-dragged value.
  const workingRef = useRef<CurvePoint[]>(working);
  const setWorkingSynced = useCallback((next: CurvePoint[]): void => {
    workingRef.current = next;
    setWorking(next);
  }, []);

  // Re-sync from props when the value changes externally (e.g. the user edits
  // the numeric table). The guard prevents clobbering our own just-emitted
  // value (which would fight an in-flight drag).
  useEffect(() => {
    const key = serialize(points);
    if (key !== lastEmitted.current) {
      setWorkingSynced(sortCurve(points));
    }
  }, [points, setWorkingSynced]);

  const maxPointKw = working.reduce((m, p) => Math.max(m, p.powerKw), 0);
  const yMax = niceCeil(Math.max(maxDcKw, maxPointKw));

  const socToX = (soc: number): number => PAD_L + (soc / 100) * PLOT_W;
  const kwToY = (kw: number): number => PLOT_BOTTOM - (yMax > 0 ? kw / yMax : 0) * PLOT_H;

  const toLocal = (clientX: number, clientY: number): { soc: number; kw: number } => {
    const svg = svgRef.current;
    if (svg === null) return { soc: 0, kw: 0 };
    const rect = svg.getBoundingClientRect();
    const vx = rect.width > 0 ? ((clientX - rect.left) / rect.width) * W : 0;
    const vy = rect.height > 0 ? ((clientY - rect.top) / rect.height) * H : 0;
    const soc = ((vx - PAD_L) / PLOT_W) * 100;
    const kw = ((PLOT_BOTTOM - vy) / PLOT_H) * yMax;
    return { soc, kw };
  };

  const commit = useCallback(
    (next: readonly CurvePoint[]): void => {
      const sorted = sortCurve(next).map((p) => snapPoint(p));
      lastEmitted.current = serialize(sorted);
      setWorkingSynced(sorted);
      onChange(sorted);
    },
    [onChange, setWorkingSynced],
  );

  // --- pointer drag ---------------------------------------------------------
  const onPointPointerDown = (i: number) => (e: React.PointerEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setSelected(i);
    draggingRef.current = i;
    svgRef.current?.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent): void => {
    const i = draggingRef.current;
    if (i === null) return;
    const { soc, kw } = toLocal(e.clientX, e.clientY);
    // movePoint preserves order → index `i` stays valid during the drag.
    setWorkingSynced(
      movePoint(workingRef.current, i, clampPoint({ socPct: soc, powerKw: kw }, yMax), yMax),
    );
  };

  const endDrag = (e: React.PointerEvent): void => {
    const i = draggingRef.current;
    if (i === null) return;
    draggingRef.current = null;
    svgRef.current?.releasePointerCapture?.(e.pointerId);
    // Read the live ref, not the (possibly stale) `working` closure — see the
    // workingRef note above. The dragged point's value lets us re-find its
    // index after sorting so selection follows the point.
    const live = workingRef.current;
    const dragged = live[i];
    const sorted = sortCurve(live).map((p) => snapPoint(p));
    lastEmitted.current = serialize(sorted);
    setWorkingSynced(sorted);
    onChange(sorted);
    if (dragged !== undefined) {
      const snappedDragged = snapPoint(dragged);
      const newIdx = sorted.findIndex(
        (p) => p.socPct === snappedDragged.socPct && p.powerKw === snappedDragged.powerKw,
      );
      setSelected(newIdx >= 0 ? newIdx : null);
    }
  };

  // --- add / remove ---------------------------------------------------------
  const onBackgroundDoubleClick = (e: React.MouseEvent): void => {
    const { soc, kw } = toLocal(e.clientX, e.clientY);
    const next = addPoint(working, { socPct: soc, powerKw: kw }, yMax);
    commit(next);
    const snapped = snapPoint(clampPoint({ socPct: soc, powerKw: kw }, yMax));
    setSelected(next.findIndex((p) => p.socPct === snapped.socPct && p.powerKw === snapped.powerKw));
  };

  const removeSelected = (): void => {
    if (selected === null) return;
    commit(removePoint(working, selected));
    setSelected(null);
  };

  // --- keyboard -------------------------------------------------------------
  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (selected === null) return;
    const pt = working[selected];
    if (pt === undefined) return;
    const big = e.shiftKey;
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      removeSelected();
      return;
    }
    let dSoc = 0;
    let dKw = 0;
    if (e.key === 'ArrowLeft') dSoc = -(big ? 5 : 1);
    else if (e.key === 'ArrowRight') dSoc = big ? 5 : 1;
    else if (e.key === 'ArrowDown') dKw = -(big ? 5 : 1);
    else if (e.key === 'ArrowUp') dKw = big ? 5 : 1;
    else return;
    e.preventDefault();
    commit(
      movePoint(
        working,
        selected,
        clampPoint({ socPct: pt.socPct + dSoc, powerKw: pt.powerKw + dKw }, yMax),
        yMax,
      ),
    );
  };

  // --- render ---------------------------------------------------------------
  const sorted = sortCurve(working);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  // Connecting line incl. flat held ends (mirrors chargePowerKwAtSoc clamps).
  const linePts: Array<{ soc: number; kw: number }> = [];
  if (first !== undefined && last !== undefined) {
    if (first.socPct > 0) linePts.push({ soc: 0, kw: first.powerKw });
    for (const p of sorted) linePts.push({ soc: p.socPct, kw: p.powerKw });
    if (last.socPct < 100) linePts.push({ soc: 100, kw: last.powerKw });
  }
  const polyline = linePts.map((p) => `${socToX(p.soc).toFixed(1)},${kwToY(p.kw).toFixed(1)}`).join(' ');
  const selPt = selected !== null ? working[selected] : undefined;

  return (
    <div data-testid={testId ?? 'curve-editor'}>
      <div className="mb-1 flex items-baseline justify-between text-[11px] text-slate-400">
        <span>Drag a point · double-click to add · select + Delete to remove</span>
        {selPt !== undefined ? (
          <span className="font-mono text-slate-300" data-testid="curve-editor-selected">
            {Math.round(selPt.socPct)}% → {Math.round(selPt.powerKw)} kW
          </span>
        ) : (
          <span className="text-slate-600">y-axis 0–{yMax} kW</span>
        )}
      </div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="w-full touch-none select-none rounded border border-slate-700 bg-slate-950"
        role="application"
        aria-label="Charging curve editor: drag points, double-click to add, Delete to remove"
        tabIndex={0}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={onKeyDown}
      >
        {/* plot background — double-click target for adding points */}
        <rect
          x={PAD_L}
          y={PAD_T}
          width={PLOT_W}
          height={PLOT_H}
          fill="transparent"
          onDoubleClick={onBackgroundDoubleClick}
          data-testid="curve-editor-surface"
        />
        {/* y gridlines + labels at 0, 50%, 100% of yMax */}
        {[0, 0.5, 1].map((f) => {
          const y = PLOT_BOTTOM - f * PLOT_H;
          return (
            <g key={`y${f}`}>
              <line
                x1={PAD_L}
                y1={y}
                x2={W - PAD_R}
                y2={y}
                className="stroke-slate-800"
                strokeDasharray={f === 0 ? undefined : '2 3'}
              />
              <text x={PAD_L - 4} y={y + 3} textAnchor="end" className="fill-slate-500 text-[8px]">
                {Math.round(f * yMax)}
              </text>
            </g>
          );
        })}
        {/* x gridlines + labels at 0, 50, 100 % SoC */}
        {[0, 50, 100].map((soc) => {
          const x = socToX(soc);
          return (
            <g key={`x${soc}`}>
              <line
                x1={x}
                y1={PAD_T}
                x2={x}
                y2={PLOT_BOTTOM}
                className="stroke-slate-800"
                strokeDasharray={soc === 0 ? undefined : '2 3'}
              />
              <text x={x} y={H - 6} textAnchor="middle" className="fill-slate-500 text-[8px]">
                {soc}%
              </text>
            </g>
          );
        })}
        {/* the curve */}
        {linePts.length >= 2 ? (
          <polyline
            points={polyline}
            fill="none"
            strokeWidth={2}
            className="stroke-primary-base"
            data-testid="curve-editor-line"
          />
        ) : null}
        {/* draggable points (in sorted display order) */}
        {sorted.map((p, i) => {
          const isSel = selPt !== undefined && p.socPct === selPt.socPct && p.powerKw === selPt.powerKw;
          // Map this sorted point back to its index in `working` for drag/select.
          const workIdx = working.findIndex((q) => q.socPct === p.socPct && q.powerKw === p.powerKw);
          return (
            <circle
              key={`pt-${i}`}
              cx={socToX(p.socPct)}
              cy={kwToY(p.powerKw)}
              r={isSel ? 6 : 4.5}
              className={
                isSel
                  ? 'cursor-grab fill-sky-300 stroke-sky-100'
                  : 'cursor-grab fill-primary-base stroke-slate-200'
              }
              strokeWidth={isSel ? 2 : 1}
              onPointerDown={onPointPointerDown(workIdx)}
              onClick={() => setSelected(workIdx)}
              data-testid={`curve-editor-point-${i}`}
              data-soc={p.socPct}
              data-kw={p.powerKw}
            />
          );
        })}
      </svg>
      <div className="mt-1 flex items-center gap-2 text-[11px]">
        <button
          type="button"
          onClick={removeSelected}
          disabled={selected === null}
          className="rounded bg-slate-700 px-2 py-0.5 text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
          data-testid="curve-editor-remove"
        >
          Remove point
        </button>
        <button
          type="button"
          onClick={() => commit(addPoint(working, { socPct: 50, powerKw: yMax / 2 }, yMax))}
          className="rounded bg-slate-700 px-2 py-0.5 text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300"
          data-testid="curve-editor-add"
        >
          + Add point
        </button>
        <span className="text-slate-600">SoC % → kW</span>
      </div>
    </div>
  );
}
